import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  serviceMetricsApi,
  CreateServiceMetricDto,
  CreatePackingMetricDto,
  CollectionDeliveryCalculation,
  SiteServiceCalculation,
  WarehouseCalculation,
  PackingCalculationType,
} from "./service-metrics.api";

// Service Metrics queries
export const useGetServiceMetrics = (clientId?: string) => {
  return useQuery({
    queryKey: ["service-metrics", clientId],
    queryFn: () => serviceMetricsApi.getServiceMetrics(clientId),
    select: (data) => data.data,
  });
};

export const useGetServiceMetricById = (id: string) => {
  return useQuery({
    queryKey: ["service-metric", id],
    queryFn: () => serviceMetricsApi.getServiceMetricById(id),
    select: (data) => data.data,
    enabled: !!id,
  });
};

export const useCreateServiceMetric = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateServiceMetricDto) =>
      serviceMetricsApi.createServiceMetric(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["service-metrics"] });
      toast.success("Service metric created successfully");
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message || "Failed to create service metric"
      );
    },
  });
};

export const useUpdateServiceMetric = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<CreateServiceMetricDto>;
    }) => serviceMetricsApi.updateServiceMetric(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["service-metrics"] });
      toast.success("Service metric updated successfully");
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message || "Failed to update service metric"
      );
    },
  });
};

export const useDeleteServiceMetric = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => serviceMetricsApi.deleteServiceMetric(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["service-metrics"] });
      toast.success("Service metric deleted successfully");
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message || "Failed to delete service metric"
      );
    },
  });
};

// Packing Metrics queries
export const useGetPackingMetrics = (clientId?: string) => {
  return useQuery({
    queryKey: ["packing-metrics", clientId],
    queryFn: () => serviceMetricsApi.getPackingMetrics(clientId),
    select: (data) => data.data,
  });
};

export const useCreatePackingMetric = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreatePackingMetricDto) =>
      serviceMetricsApi.createPackingMetric(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["packing-metrics"] });
      toast.success("Packing metric created successfully");
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message || "Failed to create packing metric"
      );
    },
  });
};

export const useUpdatePackingMetric = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<CreatePackingMetricDto>;
    }) => serviceMetricsApi.updatePackingMetric(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["packing-metrics"] });
      toast.success("Packing metric updated successfully");
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message || "Failed to update packing metric"
      );
    },
  });
};

export const useDeletePackingMetric = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => serviceMetricsApi.deletePackingMetric(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["packing-metrics"] });
      toast.success("Packing metric deleted successfully");
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message || "Failed to delete packing metric"
      );
    },
  });
};

// Calculation queries
export const useCalculateCollectionDelivery = () => {
  return useMutation({
    mutationFn: ({
      data,
      clientId,
    }: {
      data: CollectionDeliveryCalculation;
      clientId?: string;
    }) => serviceMetricsApi.calculateCollectionDelivery(data, clientId),
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message ||
          "Failed to calculate collection/delivery cost"
      );
    },
  });
};

export const useCalculateSiteService = () => {
  return useMutation({
    mutationFn: ({
      data,
      clientId,
    }: {
      data: SiteServiceCalculation;
      clientId?: string;
    }) => serviceMetricsApi.calculateSiteService(data, clientId),
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message ||
          "Failed to calculate site service cost"
      );
    },
  });
};

export const useCalculateWarehouse = () => {
  return useMutation({
    mutationFn: ({
      data,
      clientId,
    }: {
      data: WarehouseCalculation;
      clientId?: string;
    }) => serviceMetricsApi.calculateWarehouse(data, clientId),
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message || "Failed to calculate warehouse cost"
      );
    },
  });
};

export const useEstimatePackingCost = () => {
  return useMutation({
    mutationFn: ({
      length,
      width,
      height,
      clientId,
    }: {
      length: number;
      width: number;
      height: number;
      clientId?: string;
    }) =>
      serviceMetricsApi.estimatePackingCost(length, width, height, clientId),
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message || "Failed to estimate packing cost"
      );
    },
  });
};

// Packing Metric Supplies queries
export const useGetPackagingMaterials = () => {
  return useQuery({
    queryKey: ["packaging-materials"],
    queryFn: () => serviceMetricsApi.getPackagingMaterials(),
    select: (data) => data.data,
  });
};

export const useGetPackingMetricSupplies = (packingMetricId: string) => {
  return useQuery({
    queryKey: ["packing-metric-supplies", packingMetricId],
    queryFn: () => serviceMetricsApi.getPackingMetricSupplies(packingMetricId),
    select: (data) => data.data,
    enabled: !!packingMetricId,
  });
};

export const useAddPackingMetricSupply = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      packingMetricId,
      data,
    }: {
      packingMetricId: string;
      data: {
        supplyId: string;
        calculationType: PackingCalculationType;
        quantityMultiplier?: number;
        wasteFactor?: number;
      };
    }) => serviceMetricsApi.addPackingMetricSupply(packingMetricId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["packing-metric-supplies", variables.packingMetricId],
      });
      queryClient.invalidateQueries({
        queryKey: ["material-breakdown", variables.packingMetricId],
      });
      toast.success("Material added successfully");
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Failed to add material");
    },
  });
};

export const useUpdatePackingMetricSupply = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      packingMetricId,
      data,
    }: {
      id: string;
      packingMetricId: string;
      data: {
        calculationType?: PackingCalculationType;
        quantityMultiplier?: number;
        wasteFactor?: number;
      };
    }) => serviceMetricsApi.updatePackingMetricSupply(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["packing-metric-supplies", variables.packingMetricId],
      });
      queryClient.invalidateQueries({
        queryKey: ["material-breakdown", variables.packingMetricId],
      });
      toast.success("Material updated successfully");
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message || "Failed to update material"
      );
    },
  });
};

export const useRemovePackingMetricSupply = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      packingMetricId,
    }: {
      id: string;
      packingMetricId: string;
    }) => serviceMetricsApi.removePackingMetricSupply(id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["packing-metric-supplies", variables.packingMetricId],
      });
      queryClient.invalidateQueries({
        queryKey: ["material-breakdown", variables.packingMetricId],
      });
      toast.success("Material removed successfully");
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message || "Failed to remove material"
      );
    },
  });
};

export const useGetMaterialBreakdown = (
  packingMetricId: string,
  length: number,
  width: number,
  height: number,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: ["material-breakdown", packingMetricId, length, width, height],
    queryFn: () =>
      serviceMetricsApi.getMaterialBreakdown(
        packingMetricId,
        length,
        width,
        height
      ),
    select: (data) => data.data,
    enabled: enabled && !!packingMetricId && length > 0 && width > 0 && height > 0,
  });
};
